import { useState, useEffect } from 'react';
import styles from '../../styles/components/Hero.module.css';

/**
 * Section Hero réutilisable avec effet de défilement
 */
const Hero = ({ 
  title, 
  subtitle, 
  backgroundImage,
  children
}) => {
  const [scrollY, setScrollY] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calcul des valeurs dynamiques
  const maxHeight = 85; // vh
  const minHeight = 15; // vh
  const scrollThreshold = 300; // pixels
  const scrollProgress = Math.min(scrollY / scrollThreshold, 1);
  const bannerHeight = maxHeight - (scrollProgress * (maxHeight - minHeight));
  const overlayOpacity = scrollProgress * 0.5;
  const maxFontSize = 3.5; // rem
  const minFontSize = 1.1; // rem
  const titleSize = maxFontSize - (scrollProgress * (maxFontSize - minFontSize));
  const backgroundPositionY = scrollY > 300 ? -50 : 0;

  const heroStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
    backgroundPosition: `center calc(50% + ${scrollY > 300 ? -50 : 0}px)`,
    backgroundSize: 'cover',
    height: `${bannerHeight}vh`,
    zIndex: 1,
    transition: 'height 0.2s ease-out, background-position 0.2s ease-out',
    overflow: 'hidden',
    willChange: 'height'
  };

  const contentStyle = {
    position: scrollY > 300 ? 'absolute' : 'relative',
    top: scrollY > 300 ? '100px' : 'auto',
    left: scrollY > 300 ? 0 : 'auto',
    right: scrollY > 300 ? 0 : 'auto',
    zIndex: 2,
    height: scrollY > 300 ? 'auto' : '100%',
    display: 'flex',
    alignItems: scrollY > 300 ? 'flex-start' : 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease-out'
  };

  const titleStyle = {
    fontSize: `${titleSize}rem`,
    fontWeight: '700',
    color: '#fff',
    marginBottom: '1.5rem',
    letterSpacing: '-0.5px',
    textShadow: '2px 2px 8px rgba(0,0,0,0.3)',
    transition: 'font-size 0.1s ease-out',
    lineHeight: '1.2'
  };

  const subtitleStyle = {
    fontSize: '1.25rem',
    color: '#fff',
    opacity: Math.max(0, 1 - (scrollY / 200)),
    textShadow: '1px 1px 4px rgba(0,0,0,0.5)',
    transition: 'opacity 0.3s ease-out',
    maxWidth: '700px',
    margin: '0 auto',
    display: scrollY > 200 ? 'none' : 'block'
  };

  return (
    <>
      <section className={styles.hero} style={heroStyle}>
        {/* Overlay sombre pour améliorer la lisibilité */}
        <div 
          className={styles.overlay} 
          style={{
            background: `linear-gradient(135deg, rgba(1, 31, 55, ${0.6 + overlayOpacity}) 0%, rgba(1, 58, 95, ${0.4 + overlayOpacity}) 100%)`,
            transition: 'background 0.1s ease-out'
          }}
        />
        
        {/* Contenu */}
        <div style={contentStyle}>
          <div className={styles.content}>
            {title && <h1 style={titleStyle}>{title}</h1>}
            {subtitle && <p style={subtitleStyle}>{subtitle}</p>}
            {children}
          </div>
        </div>

        {/* Effet de dégradé vers le bas */}
        <div className={styles.gradientBottom} />
      </section>
      
      {/* Spacer pour pousser le contenu sous la bannière */}
      <div style={{ height: `${bannerHeight}vh` }} />
    </>
  );
};

export default Hero;
